import React, { useState, useRef, useEffect } from "react";
import { DateRangePicker, createStaticRanges } from "react-date-range";
import { startOfMonth, endOfMonth, subMonths, format, parseISO } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

// One-click whole-month shortcuts: last 12 months, most recent first.
// Each sets the range to the full calendar month (1st → last day).
const MONTH_RANGES = createStaticRanges(
   Array.from({ length: 12 }, (_, i) => {
      const m = subMonths(new Date(), i);
      return {
         label: format(m, "MMM yyyy"),
         range: () => ({ startDate: startOfMonth(m), endDate: endOfMonth(m) }),
      };
   })
);

// Blank-by-default date-range field for the report param bar.
// Shows a placeholder until the user picks; reports ISO yyyy-MM-dd strings via onChange.
export default function ReportDateRange({ value, onChange }) {
   const hasValue = Boolean(value?.from && value?.to);
   const [isOpen, setIsOpen] = useState(false);
   const ref = useRef();
   const pendingStartRef = useRef(false);

   useEffect(() => {
      const onClickOutside = (e) => {
         if (ref.current && !ref.current.contains(e.target)) {
            setIsOpen(false);
            pendingStartRef.current = false;
         }
      };
      document.addEventListener("mousedown", onClickOutside);
      return () => document.removeEventListener("mousedown", onClickOutside);
   }, []);

   // react-date-range always needs a concrete range to render the calendar. When nothing
   // is chosen yet, seed it on the current month for navigation but keep the field blank.
   const selection = {
      startDate: hasValue ? parseISO(value.from) : new Date(),
      endDate: hasValue ? parseISO(value.to) : new Date(),
      key: "selection",
   };

   const handleChange = (r) => {
      const sel = r.selection;
      onChange({
         from: format(sel.startDate, "yyyy-MM-dd"),
         to: format(sel.endDate, "yyyy-MM-dd"),
      });
      const sameDay = sel.startDate.getTime() === sel.endDate.getTime();
      if (!sameDay) {
         // Multi-day range (month shortcut or second calendar click) — complete.
         setIsOpen(false);
         pendingStartRef.current = false;
      } else if (pendingStartRef.current) {
         // Second click on the same day — single-day range complete.
         setIsOpen(false);
         pendingStartRef.current = false;
      } else {
         // First calendar click — keep open for the end date.
         pendingStartRef.current = true;
      }
   };

   const displayValue = hasValue ? `${value.from} – ${value.to}` : "";

   return (
      <div style={{ position: "relative" }} ref={ref}>
         <input
            type="text"
            readOnly
            className="form-control"
            placeholder="Select date range"
            data-testid="report-date-range"
            style={{ width: 220, cursor: "pointer" }}
            value={displayValue}
            onClick={() => {
               if (!isOpen) pendingStartRef.current = false;
               setIsOpen((o) => !o);
            }}
         />
         {isOpen && (
            <div
               style={{
                  position: "absolute",
                  zIndex: 20,
                  background: "#fff",
                  border: "1px solid #ccc",
               }}
            >
               <DateRangePicker
                  ranges={[selection]}
                  onChange={handleChange}
                  staticRanges={MONTH_RANGES}
                  inputRanges={[]}
               />
            </div>
         )}
      </div>
   );
}
